import type { RouteObject } from 'react-router';
import { CisoDashboardPage } from './pages/DashboardPage.tsx';
import { ControlsPage } from './pages/ControlsPage.tsx';
import { ControlDetailPage } from './pages/ControlDetailPage.tsx';
import { AuditLogPage } from './pages/AuditLogPage.tsx';
import { AccountPage } from './pages/AccountPage.tsx';
import { TenantNoticesPage, TenantNoticePage } from './pages/NoticesPages.tsx';
import { UsersPage } from './pages/UsersPage.tsx';
import { AuthSettingsPage } from './pages/AuthSettingsPage.tsx';
import { TenantsPage, TenantNewPage, TenantPage } from './pages/platform/TenantsPages.tsx';
import { NoticesPage, NoticeNewPage, NoticeDetailPage } from './pages/platform/NoticesPages.tsx';
import { PlatformAuditPage } from './pages/platform/PlatformAuditPage.tsx';
import { OperatorsPage, OperatorInvitePage, TenantUsersPage, TenantUserInvitePage } from './pages/platform/AccountsPages.tsx';
import { HomePage } from './pages/HomePage.tsx';
import { DomainsPage, PlatformUsersPage, SupportSessionsPage } from './pages/platform/GridsPages.tsx';
import { PlatformAccountsPage, PlatformAccountPage } from './pages/platform/PlatformAccountsPages.tsx';
import { BrandingSettingsPage } from './pages/BrandingSettingsPage.tsx';
import { OrgPage, DeptFormPage } from './pages/settings/OrgPages.tsx';
import { TenantDomainsPage, TenantDomainNewPage, TenantDomainDetailPage } from './pages/settings/DomainsPages.tsx';
import { RolesPage, RoleEditPage, AttributesPage, AttributeFormPage } from './pages/settings/RolesPages.tsx';
import { UserRolesPage } from './pages/settings/UserRolesPage.tsx';
import { SsoIdpFormPage, SsoTestResultPage, PlatformTenantSsoPage } from './pages/settings/SsoPages.tsx';
import { ApprovalsInboxPage } from './pages/approvals/ApprovalsInboxPage.tsx';
import { ApprovalNewPage } from './pages/approvals/ApprovalNewPage.tsx';
import { ApprovalDetailPage } from './pages/approvals/ApprovalDetailPage.tsx';
import { ApprovalSettingsPage } from './pages/approvals/ApprovalSettingsPage.tsx';
import { EvidenceItemsPage } from './pages/evidence/EvidenceItemsPage.tsx';
import { EvidenceItemPage } from './pages/evidence/EvidenceItemPage.tsx';
import { EvidenceVersionPage } from './pages/evidence/EvidenceVersionPage.tsx';
import { EvidenceRequestsPage } from './pages/evidence/EvidenceRequestsPage.tsx';
import { PoliciesPage } from './pages/policies/PoliciesPage.tsx';
import { PolicyDocumentPage } from './pages/policies/PolicyDocumentPage.tsx';
import { PolicyVersionEditPage } from './pages/policies/PolicyVersionEditPage.tsx';
import { PolicyVersionPage } from './pages/policies/PolicyVersionPage.tsx';
import { PolicyChangePage } from './pages/policies/PolicyChangePage.tsx';
import { AssessmentRunsPage } from './pages/assessments/AssessmentRunsPage.tsx';
import { AssessmentRunPage } from './pages/assessments/AssessmentRunPage.tsx';
import { RemediationTasksPage } from './pages/assessments/RemediationTasksPage.tsx';
import { RemediationTaskPage } from './pages/assessments/RemediationTaskPage.tsx';
import { ChecklistTemplatesPage } from './pages/assessments/ChecklistTemplatesPage.tsx';
import { ChecklistTemplatePage } from './pages/assessments/ChecklistTemplatePage.tsx';
import { VendorsPage } from './pages/vendors/VendorsPage.tsx';
import { VendorPage } from './pages/vendors/VendorPage.tsx';
import { OutsourcingsPage } from './pages/vendors/OutsourcingsPage.tsx';
import { OutsourcingPage } from './pages/vendors/OutsourcingPage.tsx';
import { ProvisionsPage, TransfersPage, ProvisionFormPage, TransferFormPage } from './pages/vendors/LedgerPages.tsx';
import { VendorNewPage } from './pages/vendors/VendorNewPage.tsx';
import { OutsourcingNewPage } from './pages/vendors/OutsourcingNewPage.tsx';
import { AssessmentRunNewPage } from './pages/assessments/AssessmentRunNewPage.tsx';
import { RemediationTaskNewPage } from './pages/assessments/RemediationTaskNewPage.tsx';
import { ChecklistTemplateNewPage } from './pages/assessments/ChecklistTemplateNewPage.tsx';
import { EvidenceItemNewPage } from './pages/evidence/EvidenceItemNewPage.tsx';
import { PolicyNewPage } from './pages/policies/PolicyNewPage.tsx';
import { ReviewsPage } from './pages/reviews/ReviewsPage.tsx';
import { ReviewTaskPage } from './pages/reviews/ReviewTaskPage.tsx';
import { RegulationFormPage, RegulationPage, RegulationsPage } from './pages/reviews/RegulationsPages.tsx';
import { UserInvitePage } from './pages/UserInvitePage.tsx';
import { UserDetailPage } from './pages/UserDetailPage.tsx';
import { TodosPage } from './pages/schedule/TodosPage.tsx';
import { CalendarPage } from './pages/schedule/CalendarPage.tsx';
import { ObligationsPage } from './pages/schedule/ObligationsPage.tsx';
import { ObligationFormPage } from './pages/schedule/ObligationFormPage.tsx';
import { ObligationPage } from './pages/schedule/ObligationPage.tsx';
import { NotifySettingsPage } from './pages/schedule/NotifySettingsPage.tsx';
import { PiiFilesPage, PiiFileFormPage, PiiFilePage } from './pages/ledgers/PiiFilesPages.tsx';
import { AreasPage, AreaFormPage, AreaPage } from './pages/ledgers/AreasPages.tsx';
import { PiiHandlersPage, PiiHandlerFormPage, PiiHandlerPage } from './pages/ledgers/PiiHandlersPages.tsx';
import { WifiNetworksPage, WifiNetworkFormPage, WifiNetworkPage, WifiDevicesPage, WifiDeviceFormPage, WifiDevicePage } from './pages/ledgers/WifiPages.tsx';
import { GenericLedgerListPage, GenericLedgerFormPage, GenericLedgerEntryPage } from './pages/ledgers/GenericLedgerPages.tsx';

/** 로그인 후 화면들. 주 라우터(router.tsx)와 화면 분할(SplitPane, 메모리 라우터)이 공유한다 (CR-011) */
import { registerRoutes } from './lib/routeTitles.ts';

export const APP_CHILDREN: RouteObject[] = [
  { index: true, Component: HomePage },
  { path: 'dashboard/ciso', Component: CisoDashboardPage },
  { path: 'controls', Component: ControlsPage },
  { path: 'controls/:id', Component: ControlDetailPage },
  { path: 'audit', Component: AuditLogPage },
  { path: 'approvals', Component: ApprovalsInboxPage },
  { path: 'approvals/new', Component: ApprovalNewPage },
  { path: 'approvals/:id', Component: ApprovalDetailPage },
  { path: 'settings/approvals', Component: ApprovalSettingsPage },
  { path: 'evidence', Component: EvidenceItemsPage },
  { path: 'evidence/items/new', Component: EvidenceItemNewPage },
  { path: 'evidence/items/:id', Component: EvidenceItemPage },
  { path: 'evidence/versions/:id', Component: EvidenceVersionPage },
  { path: 'evidence/requests', Component: EvidenceRequestsPage },
  { path: 'assessments/runs', Component: AssessmentRunsPage },
  { path: 'assessments/runs/new', Component: AssessmentRunNewPage },
  { path: 'assessments/runs/:id', Component: AssessmentRunPage },
  { path: 'assessments/tasks', Component: RemediationTasksPage },
  { path: 'assessments/tasks/new', Component: RemediationTaskNewPage },
  { path: 'assessments/tasks/:id', Component: RemediationTaskPage },
  { path: 'assessments/templates', Component: ChecklistTemplatesPage },
  { path: 'assessments/templates/new', Component: ChecklistTemplateNewPage },
  { path: 'assessments/templates/:id', Component: ChecklistTemplatePage },
  { path: 'vendors', Component: VendorsPage },
  { path: 'vendors/new', Component: VendorNewPage },
  { path: 'vendors/outsourcings', Component: OutsourcingsPage },
  { path: 'vendors/outsourcings/new', Component: OutsourcingNewPage },
  { path: 'vendors/outsourcings/:id', Component: OutsourcingPage },
  { path: 'vendors/provisions', Component: ProvisionsPage },
  { path: 'vendors/provisions/new', Component: ProvisionFormPage },
  { path: 'vendors/provisions/:id/edit', Component: ProvisionFormPage },
  { path: 'vendors/transfers', Component: TransfersPage },
  { path: 'vendors/transfers/new', Component: TransferFormPage },
  { path: 'vendors/transfers/:id/edit', Component: TransferFormPage },
  { path: 'vendors/:id', Component: VendorPage },
  { path: 'schedule/todos', Component: TodosPage },
  { path: 'schedule/calendar', Component: CalendarPage },
  { path: 'schedule/obligations', Component: ObligationsPage },
  { path: 'schedule/obligations/new', Component: ObligationFormPage },
  { path: 'schedule/obligations/:id/edit', Component: ObligationFormPage },
  { path: 'schedule/obligations/:id', Component: ObligationPage },
  { path: 'settings/notify', Component: NotifySettingsPage },
  { path: 'ledgers/pii-files', Component: PiiFilesPage },
  { path: 'ledgers/pii-files/new', Component: PiiFileFormPage },
  { path: 'ledgers/pii-files/:id/edit', Component: PiiFileFormPage },
  { path: 'ledgers/pii-files/:id', Component: PiiFilePage },
  { path: 'ledgers/areas', Component: AreasPage },
  { path: 'ledgers/areas/new', Component: AreaFormPage },
  { path: 'ledgers/areas/:id/edit', Component: AreaFormPage },
  { path: 'ledgers/areas/:id', Component: AreaPage },
  { path: 'ledgers/pii-handlers', Component: PiiHandlersPage },
  { path: 'ledgers/pii-handlers/new', Component: PiiHandlerFormPage },
  { path: 'ledgers/pii-handlers/:id/edit', Component: PiiHandlerFormPage },
  { path: 'ledgers/pii-handlers/:id', Component: PiiHandlerPage },
  { path: 'ledgers/wifi', Component: WifiNetworksPage },
  { path: 'ledgers/wifi/new', Component: WifiNetworkFormPage },
  { path: 'ledgers/wifi/devices', Component: WifiDevicesPage },
  { path: 'ledgers/wifi/devices/new', Component: WifiDeviceFormPage },
  { path: 'ledgers/wifi/devices/:id/edit', Component: WifiDeviceFormPage },
  { path: 'ledgers/wifi/devices/:id', Component: WifiDevicePage },
  { path: 'ledgers/wifi/:id/edit', Component: WifiNetworkFormPage },
  { path: 'ledgers/wifi/:id', Component: WifiNetworkPage },
  { path: 'ledgers/g/:code', Component: GenericLedgerListPage },
  { path: 'ledgers/g/:code/new', Component: GenericLedgerFormPage },
  { path: 'ledgers/g/:code/:id/edit', Component: GenericLedgerFormPage },
  { path: 'ledgers/g/:code/:id', Component: GenericLedgerEntryPage },
  { path: 'policies', Component: PoliciesPage },
  { path: 'policies/new', Component: PolicyNewPage },
  { path: 'policies/reviews', Component: ReviewsPage },
  { path: 'policies/reviews/:id', Component: ReviewTaskPage },
  { path: 'policies/regulations', Component: RegulationsPage },
  { path: 'policies/regulations/new', Component: RegulationFormPage },
  { path: 'policies/regulations/:id', Component: RegulationPage },
  { path: 'policies/:id', Component: PolicyDocumentPage },
  { path: 'policies/versions/:id', Component: PolicyVersionPage },
  { path: 'policies/versions/:id/edit', Component: PolicyVersionEditPage },
  { path: 'policies/changes/:id', Component: PolicyChangePage },
  { path: 'account', Component: AccountPage },
  { path: 'notices', Component: TenantNoticesPage },
  { path: 'notices/:id', Component: TenantNoticePage },
  { path: 'users', Component: UsersPage },
  { path: 'users/invite', Component: UserInvitePage },
  { path: 'users/:id', Component: UserDetailPage },
  { path: 'settings/auth', Component: AuthSettingsPage },
  { path: 'settings/auth/sso/new', Component: SsoIdpFormPage }, // CR-065
  { path: 'settings/auth/sso/edit', Component: SsoIdpFormPage },
  { path: 'settings/auth/sso/test/:stateId', Component: SsoTestResultPage },
  { path: 'platform', Component: TenantsPage },
  { path: 'platform/tenants', Component: TenantsPage },
  { path: 'platform/tenants/new', Component: TenantNewPage },
  { path: 'platform/tenants/:id', Component: TenantPage },
  { path: 'platform/tenants/:id/users', Component: TenantUsersPage },
  { path: 'platform/tenants/:id/sso', Component: PlatformTenantSsoPage }, // CR-066
  { path: 'platform/tenants/:id/sso/edit', element: <SsoIdpFormPage platform /> },
  { path: 'platform/tenants/:id/sso/test/:stateId', element: <SsoTestResultPage platform /> },
  { path: 'platform/tenants/:id/users/invite', Component: TenantUserInvitePage },
  { path: 'platform/users', Component: PlatformUsersPage },
  { path: 'platform/accounts', Component: PlatformAccountsPage }, // CR-067
  { path: 'platform/accounts/:id', Component: PlatformAccountPage },
  { path: 'platform/support', Component: SupportSessionsPage },
  { path: 'platform/domains', Component: DomainsPage },
  { path: 'platform/operators', Component: OperatorsPage },
  { path: 'platform/operators/invite', Component: OperatorInvitePage },
  { path: 'platform/notices', Component: NoticesPage },
  { path: 'platform/notices/new', Component: NoticeNewPage },
  { path: 'platform/notices/:id', Component: NoticeDetailPage },
  { path: 'platform/audit', Component: PlatformAuditPage },
  { path: 'settings/branding', Component: BrandingSettingsPage },
  { path: 'settings/domains', Component: TenantDomainsPage },
  { path: 'settings/domains/new', Component: TenantDomainNewPage },
  { path: 'settings/domains/:id', Component: TenantDomainDetailPage },
  { path: 'settings/org', Component: OrgPage },
  { path: 'settings/org/new', Component: DeptFormPage },
  { path: 'settings/org/:id/edit', Component: DeptFormPage },
  { path: 'settings/roles', Component: RolesPage },
  { path: 'settings/roles/new', Component: RoleEditPage },
  { path: 'settings/roles/:id', Component: RoleEditPage },
  { path: 'settings/attributes', Component: AttributesPage },
  { path: 'settings/attributes/new', Component: AttributeFormPage },
  { path: 'settings/attributes/:id/edit', Component: AttributeFormPage },
  { path: 'users/:id/roles', Component: UserRolesPage },
  { path: 'platform/tenants/:tid/users/:id/roles', element: <UserRolesPage platform /> },
];
registerRoutes(APP_CHILDREN);
